import { RealEstateDto } from 'api/real-estate';
import { NsKey } from 'enums/NsKey';
import {
  formatAddress,
  formatPropertyType
} from 'features/subscription-process/home-insurance/utils/realEstateFormatters';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { Home, MapPin, Pencil, Repeat } from 'lucide-react';
import { FC } from 'react';

interface BorrowerRealEstateSummaryProps {
  bien: RealEstateDto;
  onEdit: () => void;
  onChange: () => void;
}

export const BorrowerRealEstateSummary: FC<BorrowerRealEstateSummaryProps> = ({
  bien,
  onEdit,
  onChange
}) => {
  const { t } = useDynamicTranslation([
    NsKey.Contractualization,
    NsKey.RealEstate,
    NsKey.Common
  ]);

  return (
    <div className="p-4">
      <div className="relative mx-auto bg-white rounded-xl border overflow-hidden">
        {/* Hero */}
        <div className="relative h-40 bg-primary/80">
          <div className="flex items-center justify-center h-full text-white">
            <Home size={64} className="opacity-50" />
          </div>
          <div className="absolute inset-0 bg-black bg-opacity-20" />
          <div className="absolute bottom-4 left-4 right-4 text-white flex items-end justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl font-bold truncate">
                {bien.name || t('CONTRACTUALIZATION.REAL_ESTATE.DEFAULT_NAME')}
              </h1>
              <p className="text-sm opacity-90 flex items-center mt-1">
                <MapPin size={14} className="mr-1 shrink-0" />
                <span className="truncate">{formatAddress(bien.address)}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={onEdit}
              className="flex items-center gap-1 rounded-md bg-white/95 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-white shrink-0"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t('COMMON.EDIT')}
            </button>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="p-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-gray-800">
                {bien.area ? `${bien.area} m²` : '—'}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {t('REAL_ESTATE.LABELS.AREA')}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-gray-800">
                {bien.roomCount ?? '—'}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {t('REAL_ESTATE.LABELS.PIECES')}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-gray-800 truncate">
                {formatPropertyType(bien.propertyType, t) || '—'}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {t('REAL_ESTATE.LABELS.TYPE')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-3">
        <button
          type="button"
          onClick={onChange}
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-primary"
        >
          <Repeat className="h-4 w-4" />
          {t('CONTRACTUALIZATION.REAL_ESTATE.CHANGE_BIEN')}
        </button>
      </div>
    </div>
  );
};
